import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import type { ChatPageContext } from './types';

interface ChatContextValue {
  context: ChatPageContext | null;
  setContext: (c: ChatPageContext | null) => void;
}

const ChatCtx = createContext<ChatContextValue | undefined>(undefined);

export function ChatContextProvider({ children }: { children: ReactNode }) {
  const [context, setContextState] = useState<ChatPageContext | null>(null);
  const setContext = useCallback((c: ChatPageContext | null) => setContextState(c), []);
  const value = useMemo(() => ({ context, setContext }), [context, setContext]);
  return <ChatCtx.Provider value={value}>{children}</ChatCtx.Provider>;
}

export function useChatContextValue(): ChatContextValue {
  const v = useContext(ChatCtx);
  if (!v) throw new Error('useChatContextValue must be used within ChatContextProvider');
  return v;
}

/** Read-only accessor for the widget. Returns null outside a provider (widget-safe). */
export function useChatPageContext(): ChatPageContext | null {
  return useContext(ChatCtx)?.context ?? null;
}
